import { useEffect, type JSX, type ReactNode } from 'react';
import { z } from 'zod';

import { DocumentUploadZone } from '@/components/logsheets-maker/DocumentUploadZone';
import { KpiValuesInput } from '@/components/logsheets-maker/KpiValuesInput';
import { RejectionBanner } from '@/components/logsheets-maker/RejectionBanner';
import { StagedFilesPicker } from '@/components/logsheets-maker/StagedFilesPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { TimeField } from '@/components/ui/time-field';
import { useZodForm } from '@/lib/forms/useZodForm';
import { formatMachineName } from '@/lib/utils/machine-label';
import type {
  CreateLogsheetMakerBody,
  EntryType,
  KpiValuesShape,
  LogsheetMakerDetail,
  UpdateLogsheetMakerBody,
} from '@/types/logsheets-maker';
import type { CustomField } from '@/types/machine-types';

/**
 * Plan 12-03 — LogsheetEntryForm
 *
 * Shared between Create-wizard step 2 (`mode='create'`) and Edit page
 * (`mode='edit'`). The Detail page renders a separate read-only renderer
 * (this form does not ship a `view` mode in v1.0 — Detail uses dedicated
 * inline read-only display per D-113).
 *
 * Layout:
 *  - Read-only summary panel (Site / Machine / Type) — always visible.
 *  - RejectionBanner (only when `mode='edit' && initial.status === 'Rejected'`).
 *  - Date / Shift / StartTime / EndTime row.
 *  - For Work entries: WorkDescription textarea + KpiValuesInput.
 *  - For Idle entries: IdleReason `<Select>` + Notes textarea.
 *  - Notes textarea (Work mode also gets notes).
 *  - DocumentUploadZone (only in edit mode where `initial.id` is known;
 *    in create mode we pass `entryId={null}` and the zone shows the
 *    "save draft first" prompt).
 *  - Two action buttons: Save Draft (lenient validation) + Submit (strict).
 *
 * D-110: Strict mode is run when the user clicks Submit — required KPI
 * fields become "required" Zod errors. Save Draft is lenient (KPI values
 * may be partial). The form schema uses `superRefine` to gate the strict
 * checks behind a runtime flag.
 */

const SHIFT_OPTIONS = ['Morning', 'Afternoon', 'Night', 'Custom'] as const;
const IDLE_REASON_OPTIONS = [
  'Maintenance',
  'Breakdown',
  'Weather',
  'NoLoad',
  'FuelShortage',
  'Other',
] as const;

const baseSchema = z.object({
  entryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date is required (YYYY-MM-DD)'),
  shift: z.string().nullable(),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().nullable(),
  workDescription: z.string().max(2000).nullable(),
  idleReason: z.string().max(200).nullable(),
  notes: z.string().max(1000).nullable(),
  kpiValues: z.record(z.string(), z.unknown()),
});

type FormValues = z.infer<typeof baseSchema>;

/**
 * Start/End time are stored on the form as a wall-clock ISO string
 * (`YYYY-MM-DDTHH:mm:00Z`) — the exact shape the bulk-create path and the API
 * already use. We deliberately do NOT convert through `new Date(...)`: these are
 * site wall-clock timestamps ("machine started at 08:00"), not absolute instants,
 * so any UTC conversion would shift the value by the maker's offset (the old
 * `datetime-local` bug: pick 08:00 → stored/redisplayed as 02:30 in IST).
 */

/** Pull the `HH:mm` time-of-day out of a stored wall-clock ISO string. */
function extractHm(iso: string | null | undefined): string {
  if (iso == null) return '';
  const match = /T(\d{2}):(\d{2})/.exec(iso);
  if (match === null) return '';
  const [, hh, mm] = match;
  return hh === undefined || mm === undefined ? '' : `${hh}:${mm}`;
}

/** Combine an entry date (`YYYY-MM-DD`) + `HH:mm` into the stored wall-clock ISO. */
function buildWallClockIso(date: string, hm: string): string {
  return `${date}T${hm}:00Z`;
}

/** Local current time as `HH:mm` (wall-clock, no timezone conversion). */
function localNowHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export interface LogsheetEntryFormContext {
  readonly siteId: number;
  readonly siteName: string;
  readonly machineId: number;
  readonly machineName: string;
  readonly machineCode: string;
  /** Denormalized `vehicle_number` custom-field value; null when unset. */
  readonly vehicleNumber: string | null;
  readonly machineTypeId: number;
  readonly machineTypeName: string;
  readonly type: EntryType;
  readonly schema: readonly CustomField[];
}

export interface LogsheetEntryFormProps {
  readonly mode: 'create' | 'edit';
  readonly initial?: LogsheetMakerDetail;
  readonly context: LogsheetEntryFormContext;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
  readonly onSaveDraft: (
    body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody,
  ) => void | Promise<void>;
  readonly onSubmit: (
    body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody,
  ) => void | Promise<void>;
  readonly onUploadComplete?: () => void;
  /**
   * When true, the form omits the inner MachineType-schema KPI section.
   * Used by the WO-path wizard (D-162) which renders WO-scoped KPI inputs
   * via the `kpiSlot` prop so they appear inside Section 3 alongside the
   * work description / notes.
   */
  readonly hideKpiSection?: boolean;
  /**
   * Optional node injected into Section 3 (Details) for WO-scoped KPI inputs
   * that live outside the form's react-hook-form state. Rendered between the
   * work description and notes fields.
   */
  readonly kpiSlot?: ReactNode;
  /**
   * Inline-attach (mode='create' only). When provided, a staged-files picker
   * renders below Section 3. Parent owns the array and is responsible for
   * uploading each file via uploadStagedFiles after the create POST returns.
   * Edit mode keeps the existing DocumentUploadZone (after-save uploader);
   * passing these props in edit mode is a no-op.
   */
  readonly stagedFiles?: readonly File[];
  readonly onStagedFilesChange?: (next: File[]) => void;
}

function buildDefaults(
  context: LogsheetEntryFormContext,
  initial: LogsheetMakerDetail | undefined,
): FormValues {
  if (initial !== undefined) {
    return {
      entryDate: initial.entryDate,
      shift: initial.shift,
      startTime: initial.startTime,
      endTime: initial.endTime,
      workDescription: initial.workDescription,
      idleReason: initial.idleReason,
      notes: initial.notes,
      kpiValues: { ...initial.kpiValues },
    };
  }
  const dateDefault = new Date().toISOString().slice(0, 10);
  return {
    entryDate: dateDefault,
    shift: 'Morning',
    startTime: buildWallClockIso(dateDefault, localNowHm()),
    endTime: null,
    workDescription: context.type === 'Work' ? '' : null,
    idleReason: context.type === 'Idle' ? '' : null,
    notes: '',
    kpiValues: {},
  };
}

function applyStrictKpi(
  schema: readonly CustomField[],
  values: Record<string, unknown>,
): { fieldKey: string; message: string } | null {
  for (const f of schema) {
    const v = values[f.key];
    if (f.required && (v === undefined || v === null || v === '')) {
      return { fieldKey: f.key, message: 'Required' };
    }
    if (v === undefined || v === null || v === '') continue;
    if (f.dataType === 'dropdown' && f.dropdownOptions !== null) {
      if (typeof v !== 'string' || !f.dropdownOptions.includes(v)) {
        return { fieldKey: f.key, message: 'Pick a valid option' };
      }
    } else if (f.dataType === 'number') {
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        return { fieldKey: f.key, message: 'Must be a number' };
      }
    } else if (f.dataType === 'date') {
      if (typeof v !== 'string' || Number.isNaN(Date.parse(v))) {
        return { fieldKey: f.key, message: 'Must be a valid date' };
      }
    }
  }
  return null;
}

export function LogsheetEntryForm({
  mode,
  initial,
  context,
  submitting = false,
  errorMessage,
  onSaveDraft,
  onSubmit,
  onUploadComplete,
  hideKpiSection,
  kpiSlot,
  stagedFiles,
  onStagedFilesChange,
}: LogsheetEntryFormProps): JSX.Element {
  const defaults = buildDefaults(context, initial);
  const form = useZodForm({
    schema: baseSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when the initial entry hydrates async (edit page).
  useEffect(() => {
    if (initial !== undefined) {
      form.reset(buildDefaults(context, initial));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.id]);

  // Registered once so the entry-date input can call RHF's onChange AND run our
  // own start/end date realignment in the same handler.
  const entryDateField = form.register('entryDate');

  function bodyFromValues(values: FormValues): UpdateLogsheetMakerBody {
    return {
      entryDate: values.entryDate,
      shift: values.shift === '' ? null : values.shift,
      startTime: values.startTime,
      endTime: values.endTime === '' ? null : values.endTime,
      workDescription:
        context.type === 'Work'
          ? values.workDescription === ''
            ? null
            : values.workDescription
          : null,
      idleReason:
        context.type === 'Idle' ? (values.idleReason === '' ? null : values.idleReason) : null,
      notes: values.notes === '' ? null : values.notes,
      kpiValues: values.kpiValues as KpiValuesShape,
    };
  }

  function createBody(values: FormValues, status: 'Draft' | 'Submitted'): CreateLogsheetMakerBody {
    const body = bodyFromValues(values);
    return {
      siteId: context.siteId,
      machineId: context.machineId,
      type: context.type,
      status,
      entryDate: body.entryDate,
      shift: body.shift,
      startTime: body.startTime,
      endTime: body.endTime,
      workDescription: body.workDescription,
      idleReason: body.idleReason,
      notes: body.notes,
      kpiValues: body.kpiValues,
    };
  }

  function clearKpiErrors(): void {
    for (const f of context.schema) {
      form.clearErrors(`kpiValues.${f.key}`);
    }
  }

  function handleSaveDraft(): void {
    void form.handleSubmit((values) => {
      clearKpiErrors();
      const payload = mode === 'create' ? createBody(values, 'Draft') : bodyFromValues(values);
      void onSaveDraft(payload);
    })();
  }

  function handleSubmit(): void {
    void form.handleSubmit((values) => {
      clearKpiErrors();
      // Strict-mode validation runs when the user clicks Submit.
      if (context.type === 'Work') {
        const kpiErr = applyStrictKpi(context.schema, values.kpiValues);
        if (kpiErr !== null) {
          form.setError(`kpiValues.${kpiErr.fieldKey}`, {
            type: 'custom',
            message: kpiErr.message,
          });
          return;
        }
        const wd = values.workDescription;
        if (wd === null || wd.trim() === '') {
          form.setError('workDescription', { type: 'custom', message: 'Required for Work' });
          return;
        }
      } else {
        const ir = values.idleReason;
        if (ir === null || ir.trim() === '') {
          form.setError('idleReason', { type: 'custom', message: 'Required for Idle' });
          return;
        }
      }
      const payload = mode === 'create' ? createBody(values, 'Submitted') : bodyFromValues(values);
      void onSubmit(payload);
    })();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="space-y-6"
      noValidate
      data-testid={`logsheet-entry-form-${mode}`}
    >
      {/* Rejection banner (edit mode only). */}
      {mode === 'edit' && initial?.status === 'Rejected' && initial.rejectionReason !== null && (
        <RejectionBanner
          rejectionReason={initial.rejectionReason}
          rejectedByUserName={initial.rejectedByUserName}
          rejectedAt={initial.rejectedAt}
        />
      )}

      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="lef-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      {/* ─── Section 1 · Context ─── Site / Machine / Entry type (read-only).
          D-107 snapshot semantics; backend rejects mutation either way. */}
      <section className="space-y-3" data-testid="lef-section-context">
        <header>
          <h3 className="text-base font-semibold leading-none tracking-tight">Context</h3>
          <p className="mt-1 text-xs text-muted-foreground">Where this entry is being recorded.</p>
        </header>
        <div
          className="grid grid-cols-1 gap-3 rounded-md border border-input bg-muted/40 p-4 text-sm sm:grid-cols-3"
          data-testid="lef-summary"
        >
          <div>
            <div className="text-xs text-muted-foreground">Site</div>
            <div data-testid="lef-summary-site">{context.siteName}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Machine</div>
            <div data-testid="lef-summary-machine">
              {formatMachineName(context.machineName, context.vehicleNumber, context.machineCode)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Entry type</div>
            <div data-testid="lef-summary-type">{context.type}</div>
          </div>
        </div>
      </section>

      {/* ─── Section 2 · When ─── Date / Shift / Start time / End time. */}
      <section className="space-y-3 border-t pt-6" data-testid="lef-section-when">
        <header>
          <h3 className="text-base font-semibold leading-none tracking-tight">When</h3>
          <p className="mt-1 text-xs text-muted-foreground">Date, shift, and timing for the entry.</p>
        </header>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="lef-entry-date">Date *</Label>
            <Input
              id="lef-entry-date"
              type="date"
              disabled={submitting}
              aria-invalid={form.formState.errors.entryDate !== undefined}
              {...entryDateField}
              onChange={(e) => {
                void entryDateField.onChange(e);
                // Keep the start/end wall-clock dates aligned with the entry date
                // so the submitted timestamps land on the day the maker chose.
                const date = e.target.value;
                const startHm = extractHm(form.getValues('startTime'));
                if (startHm !== '') {
                  form.setValue('startTime', buildWallClockIso(date, startHm));
                }
                const endHm = extractHm(form.getValues('endTime'));
                if (endHm !== '') {
                  form.setValue('endTime', buildWallClockIso(date, endHm));
                }
              }}
              data-testid="lef-entry-date"
            />
            {form.formState.errors.entryDate && (
              <p className="text-xs text-destructive" data-testid="lef-entry-date-error">
                {form.formState.errors.entryDate.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="lef-shift-trigger">Shift</Label>
            <Select
              value={
                form.watch('shift') === null || form.watch('shift') === ''
                  ? 'Custom'
                  : (form.watch('shift') ?? 'Custom')
              }
              onValueChange={(v) => {
                form.setValue('shift', v);
              }}
              disabled={submitting}
            >
              <SelectTrigger id="lef-shift-trigger" data-testid="lef-shift">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHIFT_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`lef-shift-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="lef-start-time-hour">Start time *</Label>
            {/* Time-of-day only — the day comes from the Date field above and is
                combined into a wall-clock ISO on change. A custom control (not
                native <input type="time">) so the 12-hour format is identical on
                macOS and Windows, and so nothing round-trips through Date/UTC. */}
            <TimeField
              id="lef-start-time-hour"
              testId="lef-start-time"
              disabled={submitting}
              invalid={form.formState.errors.startTime !== undefined}
              value={extractHm(form.watch('startTime'))}
              onChange={(hm) => {
                const date = form.getValues('entryDate');
                form.setValue('startTime', hm === '' ? '' : buildWallClockIso(date, hm));
                if (form.formState.errors.startTime) form.clearErrors('startTime');
              }}
            />
            {form.formState.errors.startTime && (
              <p className="text-xs text-destructive" data-testid="lef-start-time-error">
                {form.formState.errors.startTime.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="lef-end-time-hour">End time</Label>
            <TimeField
              id="lef-end-time-hour"
              testId="lef-end-time"
              disabled={submitting}
              value={extractHm(form.watch('endTime'))}
              onChange={(hm) => {
                const date = form.getValues('entryDate');
                form.setValue('endTime', hm === '' ? null : buildWallClockIso(date, hm));
              }}
            />
          </div>
        </div>
      </section>

      {/* ─── Section 3 · Details ─── Work description (or Idle reason) +
          KPI values + Notes. KPIs land here so they live alongside the
          captured work narrative rather than floating above the context. */}
      <section className="space-y-4 border-t pt-6" data-testid="lef-section-details">
        <header>
          <h3 className="text-base font-semibold leading-none tracking-tight">Details</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {context.type === 'Work'
              ? 'What was done, the measured values, and any notes.'
              : 'Reason the machine was idle and any notes.'}
          </p>
        </header>

        {context.type === 'Work' ? (
          <div className="space-y-1">
            <Label htmlFor="lef-work-description">Work description *</Label>
            <Textarea
              id="lef-work-description"
              disabled={submitting}
              value={form.watch('workDescription') ?? ''}
              onChange={(e) => {
                form.setValue('workDescription', e.target.value);
              }}
              data-testid="lef-work-description"
              rows={3}
            />
            {form.formState.errors.workDescription && (
              <p className="text-xs text-destructive" data-testid="lef-work-description-error">
                {form.formState.errors.workDescription.message}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <Label htmlFor="lef-idle-reason-trigger">Idle reason *</Label>
            <Select
              value={form.watch('idleReason') ?? ''}
              onValueChange={(v) => {
                form.setValue('idleReason', v);
              }}
              disabled={submitting}
            >
              <SelectTrigger id="lef-idle-reason-trigger" data-testid="lef-idle-reason-trigger">
                <SelectValue placeholder="Select reason…" />
              </SelectTrigger>
              <SelectContent>
                {IDLE_REASON_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`lef-idle-reason-option-${r}`}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.idleReason && (
              <p className="text-xs text-destructive" data-testid="lef-idle-reason-error">
                {form.formState.errors.idleReason.message}
              </p>
            )}
          </div>
        )}

        {context.type === 'Work' && hideKpiSection !== true && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">KPI values</Label>
            <KpiValuesInput
              schema={context.schema}
              control={form.control}
              setValue={form.setValue}
              errors={form.formState.errors}
              disabled={submitting}
            />
          </div>
        )}

        {hideKpiSection === true && kpiSlot !== undefined && (
          <div data-testid="lef-kpi-slot">{kpiSlot}</div>
        )}

        <div className="space-y-1">
          <Label htmlFor="lef-notes">Notes</Label>
          <Textarea
            id="lef-notes"
            disabled={submitting}
            value={form.watch('notes') ?? ''}
            onChange={(e) => {
              form.setValue('notes', e.target.value);
            }}
            data-testid="lef-notes"
            rows={2}
          />
        </div>
      </section>

      {mode === 'edit' && initial !== undefined && (
        <section className="space-y-2 border-t pt-6" data-testid="lef-section-documents">
          <Label className="text-sm font-medium">Documents</Label>
          <DocumentUploadZone
            entryId={initial.id}
            status={initial.status}
            documents={initial.documents}
            onUploadComplete={onUploadComplete ?? ((): void => undefined)}
          />
        </section>
      )}

      {mode === 'create' && stagedFiles !== undefined && onStagedFilesChange !== undefined && (
        <section
          className="space-y-2 border-t pt-6"
          data-testid="lef-section-staged-documents"
        >
          <Label className="text-sm font-medium">Documents</Label>
          <p className="text-xs text-muted-foreground">
            Files attach after the entry is saved.
          </p>
          <StagedFilesPicker
            files={stagedFiles}
            onChange={onStagedFilesChange}
            disabled={submitting}
            idPrefix="lef-staged"
            testIdPrefix="lef-staged-files"
          />
        </section>
      )}

      <div className="flex items-center gap-2 border-t pt-6">
        <Button
          type="button"
          variant="outline"
          disabled={submitting}
          onClick={handleSaveDraft}
          data-testid="lef-save-draft"
        >
          {submitting ? 'Saving…' : 'Save Draft'}
        </Button>
        <Button type="submit" disabled={submitting} data-testid="lef-submit">
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </div>
    </form>
  );
}
